import axios, { type AxiosInstance } from "axios";

import {
  attachHttpInterceptors,
  type HttpInterceptorsOptions,
} from "./interceptors";
import { clearHttpAccessToken, readHttpAccessToken } from "./accessToken";
import type { HttpClientError } from "./errors";
import { notifyForbiddenPermission } from "./forbiddenToast";
import { applyRetryPolicy, type HttpRetryPolicy } from "./retry";

const DEFAULT_TIMEOUT_MS = 60_000;
export const HTTP_AUTH_EXPIRED_EVENT = "flowermaster:http-auth-expired";
export const HTTP_FORBIDDEN_EVENT = "flowermaster:http-forbidden";

/** 创建 HTTP 客户端时的可选参数。 */
export type CreateHttpClientOptions = HttpInterceptorsOptions & {
  baseURL?: string;
  timeoutMs?: number;
  retry?: Partial<HttpRetryPolicy>;
};

/** 构建企业级统一 axios 客户端实例。 */
export function createHttpClient(options: CreateHttpClientOptions = {}): AxiosInstance {
  const client = axios.create({
    baseURL: options.baseURL ?? "/api",
    timeout: options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
  });

  applyRetryPolicy(client, options.retry);
  attachHttpInterceptors(client, {
    ...options,
    getAccessToken: options.getAccessToken ?? readHttpAccessToken,
    onUnauthorized: options.onUnauthorized ?? handleDefaultUnauthorized,
    onForbidden: options.onForbidden ?? handleDefaultForbidden,
  });
  return client;
}

/** 默认共享的 HTTP 客户端单例。 */
export const httpClient = createHttpClient();

/** 客户公开请求与员工请求统一走同源 /api，仅隔离凭据和错误处理。 */
export const anonymousHttpClient = createHttpClient({
  getAccessToken: () => null,
  onUnauthorized: () => undefined,
  onForbidden: () => undefined,
});
anonymousHttpClient.defaults.withCredentials = true;

export { resolveHttpResourceUrl } from "./resourceUrl";

/** 默认 401 处理：清理登录态，并通知后台壳层跳转统一登录页。 */
function handleDefaultUnauthorized(error: HttpClientError): void {
  if (isLoginRequest(error.request.url) || isPublicItineraryRequest(error)) {
    return;
  }
  clearHttpAccessToken();
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(new Event(HTTP_AUTH_EXPIRED_EVENT));
}

/** 默认 403 处理：全局提示无权限，避免页面静默失败。 */
function handleDefaultForbidden(error: HttpClientError): void {
  notifyForbiddenPermission(error.message);
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(
    new CustomEvent(HTTP_FORBIDDEN_EVENT, {
      detail: { message: error.message, url: error.request.url },
    }),
  );
}

/** 判断 401 是否来自登录请求本身，避免密码错误被误判为登录过期。 */
function isLoginRequest(url: string): boolean {
  const path = requestPath(url);
  return path === "/auth/login" || path.endsWith("/auth/login");
}

/** 判断 401 是否来自匿名行程分享读取或 PDF 生成。 */
function isPublicItineraryRequest(error: HttpClientError): boolean {
  const path = requestPath(error.request.url);
  if (error.request.method === "get") {
    return /(?:^|\/)itineraries\/public\/[^/]+$/.test(path)
      || /(?:^|\/)fulfillment-orders\/public\/itineraries\/[^/]+\/customer-output$/.test(path);
  }
  return error.request.method === "post"
    && /(?:^|\/)itineraries\/public\/[^/]+\/pdf$/.test(path);
}

/** 从相对或绝对请求地址提取不含查询参数的路径。 */
function requestPath(url: string): string {
  try {
    return new URL(url, "http://flower-master.local").pathname;
  } catch {
    return url.split("?")[0];
  }
}
